/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/lump-sum-helpers.ts
 * FILE_ID: QB-AGGRID-LUMP-SUM-001
 * PURPOSE: Pure I→O builder for a SAM-style "+ Lump sum" row. Produces
 *          a create-payload shaped like a normal line item but with
 *          quantity=1, unit="lump sum", and manual pricing mode so
 *          the row total is the override_chargeout the estimator
 *          enters directly. No backend change required.
 * WHY: SAM exposes "+ Lump sum" alongside "+ Add row" as a peer
 *      row category. Quotech's pricing engine already supports manual
 *      pricing mode — we just had no shortcut to seed a row in that
 *      shape. Estimators can use this for non-itemised costs (e.g.
 *      "Mobilisation", "Crane hire fixed", "Permit fee").
 * FLOW:
 *   ┌───────────────┐   ┌──────────────────────┐   ┌──────────────────────┐
 *   │ click + Lump  │ → │ buildLumpSumPayload  │ → │ onAddItem(section,   │
 *   │ sum button    │   │ (sortOrder, defaults)│   │   payload)           │
 *   └───────────────┘   └──────────────────────┘   └──────────────────────┘
 * RELATED:
 *   - QB-AGGRID-TOOLBAR-001 (ScopeSectionToolbar) — exposes the button
 *   - QB-AGGRID-SECTION-001 (ScopeAgGridSection) — wires the handler
 */

export interface LumpSumDefaults {
  /** Description placeholder for the new row. Defaults to "Lump sum". */
  description?: string
  /** Optional initial chargeout amount. Defaults to 0 — estimator fills in. */
  amount?: number
}

export interface LumpSumPayload {
  description: string
  quantity: number
  unit: string
  /**
   * Manual pricing mode tells the backend not to apply the rate /
   * productivity engine. The row total is whatever the estimator
   * types into override_chargeout.
   */
  pricing_mode: 'manual'
  override_chargeout: number
  /**
   * Productivity is irrelevant for lump-sum rows but the backend
   * serializer doesn't accept null in some legacy clients — sending
   * a sentinel of 0 makes the row trivially valid.
   */
  productivity_pmpd: number
  sort_order: number
  [key: string]: unknown
}

/**
 * Build a SAM-style "+ Lump sum" create-payload. Pure I→O so the
 * shape is easy to inspect + assert in tests; the section's
 * onAddItem callback owns the actual persist + refetch.
 */
export function buildLumpSumPayload(
  sortOrder: number,
  defaults: LumpSumDefaults = {},
): LumpSumPayload {
  return {
    description: defaults.description ?? 'Lump sum',
    quantity: 1,
    unit: 'lump sum',
    pricing_mode: 'manual',
    override_chargeout: defaults.amount ?? 0,
    productivity_pmpd: 0,
    sort_order: sortOrder,
  }
}
